import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Phone, ChevronDown, Check, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Country codes with flags
const COUNTRIES = [
  { code: '+1', country: 'United States', flag: '🇺🇸', abbr: 'US' },
  { code: '+1', country: 'Canada', flag: '🇨🇦', abbr: 'CA' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', abbr: 'GB' },
  { code: '+61', country: 'Australia', flag: '🇦🇺', abbr: 'AU' },
  { code: '+49', country: 'Germany', flag: '🇩🇪', abbr: 'DE' },
  { code: '+33', country: 'France', flag: '🇫🇷', abbr: 'FR' },
  { code: '+39', country: 'Italy', flag: '🇮🇹', abbr: 'IT' },
  { code: '+34', country: 'Spain', flag: '🇪🇸', abbr: 'ES' },
  { code: '+81', country: 'Japan', flag: '🇯🇵', abbr: 'JP' },
  { code: '+86', country: 'China', flag: '🇨🇳', abbr: 'CN' },
  { code: '+91', country: 'India', flag: '🇮🇳', abbr: 'IN' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽', abbr: 'MX' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', abbr: 'BR' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷', abbr: 'KR' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', abbr: 'NL' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', abbr: 'SE' },
  { code: '+47', country: 'Norway', flag: '🇳🇴', abbr: 'NO' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', abbr: 'DK' },
  { code: '+358', country: 'Finland', flag: '🇫🇮', abbr: 'FI' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', abbr: 'CH' },
  { code: '+43', country: 'Austria', flag: '🇦🇹', abbr: 'AT' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪', abbr: 'BE' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', abbr: 'PT' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪', abbr: 'IE' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', abbr: 'NZ' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', abbr: 'SG' },
  { code: '+852', country: 'Hong Kong', flag: '🇭🇰', abbr: 'HK' },
  { code: '+971', country: 'UAE', flag: '🇦🇪', abbr: 'AE' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', abbr: 'SA' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦', abbr: 'ZA' },
  { code: '+48', country: 'Poland', flag: '🇵🇱', abbr: 'PL' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿', abbr: 'CZ' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺', abbr: 'HU' },
  { code: '+30', country: 'Greece', flag: '🇬🇷', abbr: 'GR' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷', abbr: 'TR' },
  { code: '+7', country: 'Russia', flag: '🇷🇺', abbr: 'RU' },
  { code: '+380', country: 'Ukraine', flag: '🇺🇦', abbr: 'UA' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭', abbr: 'PH' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', abbr: 'TH' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', abbr: 'MY' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩', abbr: 'ID' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳', abbr: 'VN' },
];

const PhoneVerificationModal = ({ 
  isOpen, 
  onClose, 
  onVerified, 
  isRegistration = false,
  email = '',
  cannotClose = false 
}) => {
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Enter OTP
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [fullPhoneNumber, setFullPhoneNumber] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  
  const otpRefs = useRef([]);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(c => 
    c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.includes(countrySearch) ||
    c.abbr.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Format phone number for display (US format)
  const formatPhoneDisplay = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);
  };

  const handleSendOTP = async () => {
    if (phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    if (!smsConsent) {
      toast.error('Please agree to receive SMS messages');
      return;
    }

    setLoading(true);
    try {
      const fullPhone = selectedCountry.code + phoneNumber;
      setFullPhoneNumber(fullPhone);

      const endpoint = isRegistration 
        ? '/auth/send-phone-verification-guest'
        : '/auth/send-phone-verification';
      
      const payload = isRegistration
        ? { phone_number: phoneNumber, country_code: selectedCountry.code, email }
        : { phone_number: phoneNumber, country_code: selectedCountry.code };

      const headers = { 'Content-Type': 'application/json' };
      if (!isRegistration) {
        const token = localStorage.getItem('parseeker_session_token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }

      await axios.post(`${API}${endpoint}`, payload, { headers });
      
      setStep(2);
      setResendTimer(60);
      toast.success('Verification code sent!');
    } catch (error) {
      console.error('Send OTP error:', error);
      const detail = error.response?.data?.detail || 'Failed to send verification code';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pasted.split('').forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);
    if (pasted.length === 6) {
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isRegistration 
        ? '/auth/verify-phone-guest'
        : '/auth/verify-phone';
      
      const payload = isRegistration
        ? { phone_number: fullPhoneNumber, code, email }
        : { phone_number: fullPhoneNumber, code };

      const headers = { 'Content-Type': 'application/json' };
      if (!isRegistration) {
        const token = localStorage.getItem('parseeker_session_token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }

      await axios.post(`${API}${endpoint}`, payload, { headers });
      
      toast.success('Phone number verified!');
      onVerified?.(fullPhoneNumber);
    } catch (error) {
      console.error('Verify OTP error:', error);
      const detail = error.response?.data?.detail || 'Invalid verification code';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    await handleSendOTP();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md bg-[#1a1a1a]/95 backdrop-blur-md border-[#444] shadow-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[var(--ps-primary)]" />
            {step === 1 ? "What's your mobile number?" : "Enter verification code"}
          </DialogTitle>
        </DialogHeader>

        {/* Temporary Notice */}
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-3 mb-2">
          <p className="text-amber-200 text-xs">
            <strong>Note:</strong> SMS verification is temporarily unavailable while we complete carrier registration. You can skip this step for now.
          </p>
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              Enter the mobile number where you can be contacted. No one will see this on your profile.
            </p>

            {/* Country Selector */}
            <div className="relative" ref={dropdownRef}>
              <Label className="text-gray-400 text-xs mb-1 block">Country</Label>
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-white hover:border-green-500/70 transition-colors"
                data-testid="country-selector"
              >
                <span className="flex items-center gap-2">
                  <span className="text-xl">{selectedCountry.flag}</span>
                  <span>{selectedCountry.country} ({selectedCountry.code})</span>
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showCountryDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a]/95 backdrop-blur-md border border-white/20 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-white/10">
                    <Input
                      placeholder="Search country..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredCountries.map((country, idx) => (
                      <button
                        key={`${country.abbr}-${idx}`}
                        type="button"
                        onClick={() => {
                          setSelectedCountry(country);
                          setShowCountryDropdown(false);
                          setCountrySearch('');
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors ${
                          selectedCountry.abbr === country.abbr ? 'bg-green-900/30' : ''
                        }`}
                      >
                        <span className="text-xl">{country.flag}</span>
                        <span className="text-white">{country.country}</span>
                        <span className="text-gray-400 text-sm ml-auto">{country.code}</span>
                        {selectedCountry.abbr === country.abbr && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Phone Number Input */}
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">Mobile number</Label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 px-3 py-2.5 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-gray-300">
                  <span className="text-lg">{selectedCountry.flag}</span>
                  <span>{selectedCountry.code}</span>
                </div>
                <Input
                  type="tel"
                  value={formatPhoneDisplay(phoneNumber)}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  className="flex-1 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 backdrop-blur-sm"
                  data-testid="phone-input"
                />
              </div>
              {/* Phone Usage Note */}
              <p className="mt-2 text-amber-400 text-xs font-medium text-center">
                Your Phone Number Will Be Used To Confirm Tournament Entry and User Game Linking
              </p>
            </div>

            {/* SMS Consent Checkbox - Required for Twilio A2P compliance */}
            <div className="flex items-start gap-3 p-3 rounded-lg border border-white/20 bg-white/5 backdrop-blur-sm">
              <input
                type="checkbox"
                id="sms-consent"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-green-500 focus:ring-green-500 focus:ring-offset-0 cursor-pointer"
                data-testid="sms-consent-checkbox"
              />
              <label htmlFor="sms-consent" className="text-gray-300 text-xs leading-relaxed cursor-pointer">
                Yes, I agree to receive automated text messages from Par Seeker including account verification codes, game invitations, score notifications, and settlement reminders. Message frequency varies. Message and data rates may apply.
              </label>
            </div>

            {/* Compliance Disclosures */}
            <div className="text-gray-500 text-xs space-y-1">
              <p>Reply <span className="text-gray-300">STOP</span> to unsubscribe. Reply <span className="text-gray-300">HELP</span> for help.</p>
              <p>
                <a href="https://parseeker.com/terms" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">Terms of Service</a>
                {' '}&amp;{' '}
                <a href="https://parseeker.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">Privacy Policy</a>
              </p>
            </div>

            <Button
              onClick={handleSendOTP}
              disabled={loading || phoneNumber.length < 10 || !smsConsent}
              className="w-full py-4 rounded-xl font-black hover:scale-105 transition-all disabled:opacity-50 uppercase tracking-tight"
              style={{ 
                fontFamily: 'var(--ps-font-heading, system-ui)',
                background: '#15803d',
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.5), 0 0 40px rgba(34, 197, 94, 0.3)',
                textShadow: '0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700',
                fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                color: '#FFFFFF'
              }}
              data-testid="send-otp-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'NEXT'
              )}
            </Button>

            {/* Skip Button */}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors"
              data-testid="skip-phone-btn"
            >
              Skip for now
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              Enter the 6-digit code we sent to <span className="text-white font-medium">{fullPhoneNumber}</span>
            </p>

            {/* Temporary Notice for OTP step */}
            <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-2">
              <p className="text-amber-200 text-xs">
                If you didn't receive the code, SMS delivery may be delayed. You can skip and verify later.
              </p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border-white/20 text-white backdrop-blur-sm"
                  data-testid={`otp-input-${index}`}
                />
              ))}
            </div>

            <Button
              onClick={handleVerifyOTP}
              disabled={loading || otp.join('').length !== 6}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold"
              data-testid="verify-otp-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || loading}
                className={`text-sm ${resendTimer > 0 ? 'text-gray-500' : 'text-green-500 hover:underline'}`}
                data-testid="resend-otp-btn"
              >
                {resendTimer > 0 ? `Resend code in ${resendTimer}s` : "Didn't receive code? Resend"}
              </button>
            </div>

            <button
              onClick={() => {
                setStep(1);
                setOtp(['', '', '', '', '', '']);
              }}
              className="w-full text-sm text-gray-500 hover:text-white"
            >
              Change phone number
            </button>

            {/* Skip Button */}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-gray-400 hover:text-white text-sm border-t border-gray-700 pt-3 mt-2"
              data-testid="skip-phone-otp-btn"
            >
              Skip for now
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationModal;
